import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Los campos del panel, en un solo sitio.
 *
 * Estaban repetidos con la misma cadena de clases de Tailwind en cada
 * pantalla. Con seis formularios nuevos eso deja de ser aceptable: el dia que
 * cambie el borde de un input habria que tocar seis archivos y alguno se
 * quedaria distinto.
 */

const CONTROL =
  "w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-ui " +
  "outline-none transition-colors placeholder:text-ink-3 " +
  "focus:border-accent disabled:cursor-not-allowed disabled:opacity-50";

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-meta font-medium">
        {label}
        {required && <span className="ml-0.5 text-crit">*</span>}
      </span>
      {children}
      {error ? (
        <span className="text-xs text-crit">{error}</span>
      ) : (
        hint && <span className="text-xs text-ink-3">{hint}</span>
      )}
    </label>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cn(CONTROL, className)} />;
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={cn(CONTROL, className)} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cn(CONTROL, "resize-y", className)} />;
}

/**
 * Interruptor con su explicacion al lado. Un checkbox suelto con una etiqueta
 * de dos palabras obliga a adivinar que pasa al activarlo.
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-md border border-line-strong p-3",
        checked ? "bg-accent-soft/40" : "bg-surface",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 accent-[var(--accent)]"
      />
      <span className="min-w-0">
        <span className="block text-ui font-medium">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-ink-3">{hint}</span>}
      </span>
    </label>
  );
}

/** Rejilla de dos columnas que se pliega en movil. */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("grid gap-3.5 sm:grid-cols-2", className)}>{children}</div>
  );
}

/**
 * Barra de guardado que solo aparece cuando hay algo que guardar.
 *
 * Un boton de guardar siempre visible y siempre activo no distingue entre
 * "no he tocado nada" y "he tocado algo y no lo he guardado", que es
 * justamente lo que hay que saber antes de cerrar la pestaña.
 */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
}) {
  if (!dirty) return null;

  return (
    <div className="sticky bottom-0 z-10 -mx-px flex items-center justify-between gap-3 rounded-b-lg border-t border-line bg-surface/95 px-4 py-3 backdrop-blur">
      <span className="text-meta text-ink-2">Hay cambios sin guardar.</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={saving}
          className="rounded-md px-3 py-1.5 text-ui text-ink-2 hover:bg-sunk disabled:opacity-50"
        >
          Descartar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-md bg-accent px-3.5 py-1.5 text-ui font-medium text-accent-ink disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
